import { AxiosError } from 'axios';
import { IStop } from '@clean-code/types/stops';
import { getAxiosErrorMessage } from '@clean-code/utils/errorHandling';

export const stopHasPriorAnnotation = (stop: IStop | undefined): boolean =>
  !!(
    stop?.classification.generalCauseId ||
    stop?.classification.specificCauseId ||
    stop?.classification.componentId ||
    stop?.classification.description?.trim()
  );

const ANNOTATION_FIELD_BY_KEY: Record<string, string> = {
  generalCauseId: 'general_cause',
  specificCauseId: 'specific_cause',
  componentId: 'equipment',
  description: 'description'
};

/** What a single inline annotation edit changed, for analytics. */
export type StopAnnotationChange = {
  changedFields: string[];
  isFirstAnnotation: boolean;
};

export function describeStopAnnotationChange(
  previous: IStop,
  change: Partial<IStop['classification']>
): StopAnnotationChange {
  // Picking a cause also resets dependent fields, so compare values instead
  // of trusting every key in the change.
  const changedKeys = (
    Object.keys(change) as (keyof IStop['classification'])[]
  ).filter(
    (key) => (change[key] ?? null) !== (previous.classification[key] ?? null)
  );
  return {
    changedFields: changedKeys
      .map((key) => ANNOTATION_FIELD_BY_KEY[key])
      .filter((field): field is string => !!field),
    isFirstAnnotation: !stopHasPriorAnnotation(previous)
  };
}

/** The backend refused to change or clear a saved cause: it takes `stop.annotation.modify`. */
export const isStopCauseLockedError = (error: AxiosError | Error): boolean =>
  !!getAxiosErrorMessage(error)?.startsWith(
    'Forbidden. The stop already has a saved cause.'
  );
